import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRightLeft, Loader2, MapPin, ScanLine, Undo2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { joApi, joErr, sendToText } from '@/services/processingJoApi';
import { JoBadge, fmtN } from './JoBits';

const ColourChips = ({ l, cols, bs, prefix = '', testKey }) => cols.map((c) => {
  const mine = bs.filter((b) => b.color_name === c.color_name);
  const got = mine.filter((b) => b.picked).reduce((a, b) => a + b.pcs, 0);
  const planned = mine.reduce((a, b) => a + b.pcs, 0);
  const short = planned < c.requested_pcs;
  const id = `${l.sort_no}-${testKey}${c.color_name}`;
  return (
    <span key={`${testKey}${c.color_name}`} className={`text-[11px] rounded px-2 py-0.5 ${short ? 'bg-amber-100 text-amber-800' : testKey ? 'bg-indigo-100 text-indigo-800' : 'bg-[#2D5F4F]/10 text-[#2D5F4F]'}`} data-testid={`gs-slip-colour-${id}`}>
      {prefix}{c.color_name}: <b>{fmtN(got)}</b> / {fmtN(planned)} <span className="text-gray-500">(asked {fmtN(c.requested_pcs)})</span>
      {short && <b className="ml-1" data-testid={`gs-slip-colour-short-${id}`}>· short {fmtN(c.requested_pcs - planned)}</b>}</span>
  );
});

const ColourProgress = ({ l, mine }) => (
  <div className="flex flex-wrap gap-1 mt-1">
    <ColourChips l={l} cols={l.colors || []} bs={mine.filter((b) => !b.source_sort)} testKey="" />
    <ColourChips l={l} cols={l.parent_colors || []} bs={mine.filter((b) => b.source_sort)} prefix={`Parent ${l.parent_sort_no} · `} testKey="parent-" />
  </div>
);

const LineProgress = ({ slip }) => (
  <div className="space-y-2">
    {slip.lines.map((l) => {
      const mine = slip.bundles.filter((b) => b.sort_no === l.sort_no);
      const picked = mine.filter((b) => b.picked).reduce((a, b) => a + b.pcs, 0);
      const planned = mine.reduce((a, b) => a + b.pcs, 0);
      return (
        <div key={l.sort_no} data-testid={`gs-slip-line-${l.sort_no}`}>
          <div className="flex justify-between gap-2 text-sm"><span className="font-mono">{l.sort_no}</span>
            <span className="tabular-nums" data-testid={`gs-slip-line-picked-${l.sort_no}`}>{fmtN(picked)} / {fmtN(planned)} pcs <span className="text-gray-400">(asked {fmtN(l.requested_pcs)})</span></span></div>
          <Progress value={planned ? Math.min(100, (picked / planned) * 100) : 0} className="h-2" />
          {(l.colors?.length > 0 || l.parent_colors?.length > 0) && <ColourProgress l={l} mine={mine} />}
        </div>
      );
    })}
  </div>
);

const Slots = ({ slip, live, onUnpick }) => (
  <div className="border rounded-lg divide-y bg-white max-h-[380px] overflow-y-auto">
    {slip.bundles.map((b) => (
      <div key={b.bundle_barcode} className={`flex items-center justify-between gap-2 px-3 py-2 ${b.picked ? 'bg-emerald-50/70' : ''}`} data-testid={`gs-slip-slot-${b.bundle_barcode}`}>
        <div className="min-w-0">
          <p className="font-mono text-sm break-all">{b.bundle_barcode} <span className="text-gray-400 whitespace-nowrap">· {b.sort_no}{b.color_name ? ` · ${b.color_name}` : ''} · {b.pcs} pcs</span></p>
          <p className="text-[11px] text-gray-500 flex items-center gap-1 flex-wrap"><MapPin className="w-3 h-3" />{b.footprint || 'no location'}
            {b.substituted_from && <span className="text-amber-700">· instead of {b.substituted_from}</span>}
            {b.source_sort && <span className="text-indigo-700" data-testid={`gs-slip-slot-parent-${b.bundle_barcode}`}>· {b.converted_from ? `was parent ${b.converted_from} → now ${b.sort_no}` : `parent sort ${b.source_sort} → changes to ${b.sort_no} when picked`}</span>}
            {b.picked_by_name && <span>· {b.picked_by_name}</span>}</p>
          {b.bin_locked && <p className="text-[11px] font-medium text-rose-700" data-testid={`gs-slip-slot-locked-${b.bundle_barcode}`}>Bin locked for picking — scan another bundle of sort {b.source_sort || b.sort_no}{b.color_name ? ` in ${b.color_name}` : ''}</p>}
        </div>
        {b.picked ? (live && <Button size="sm" variant="ghost" onClick={() => onUnpick(b.bundle_barcode)} data-testid={`gs-slip-unpick-${b.bundle_barcode}`}><Undo2 className="w-3.5 h-3.5 mr-1" />Undo</Button>)
          : <span className={`text-[11px] shrink-0 ${b.bin_locked ? 'text-rose-600 font-semibold' : 'text-gray-400'}`}>{b.bin_locked ? 'locked' : 'to pick'}</span>}
      </div>
    ))}
  </div>
);

/** Picker side of a Processing job-order pick slip: scan (same sort + same colour swaps allowed) → Internal Transfer to Processing. */
export const GsSlipPanel = ({ slipId, onBack, onChanged }) => {
  const [slip, setSlip] = useState(null);
  const [scan, setScan] = useState('');
  const [busy, setBusy] = useState('');
  const [reason, setReason] = useState('');
  const ref = useRef(null);
  const load = () => joApi.slip(slipId).then(setSlip).catch((e) => toast.error(joErr(e, 'Could not open the pick slip')));
  useEffect(() => { setSlip(null); load(); }, [slipId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (slip && ['OPEN', 'PICKING'].includes(slip.status)) setTimeout(() => ref.current?.focus(), 80); }, [slip?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const r = await fn(); toast.success(typeof msg === 'function' ? msg(r) : msg); await load(); onChanged(); }
    catch (e) { toast.error(joErr(e)); } finally { setBusy(''); setTimeout(() => ref.current?.focus(), 80); }
  };
  if (!slip) return <Loader2 className="w-6 h-6 animate-spin mx-auto my-10 text-[#2D5F4F]" />;
  const live = ['OPEN', 'PICKING', 'PICKED'].includes(slip.status);
  const onScan = (e) => { e.preventDefault(); const code = scan.trim(); if (!code) return; setScan('');
    run('pick', () => joApi.pick(slip.slip_id, code), (r) => `${r.bundle_code} picked (${r.pcs} pcs)${r.substituted_from ? ` instead of ${r.substituted_from}` : ''}${r.converted_from ? ` · changed ${r.converted_from} → ${r.sort_no}` : ''}`); };

  return (
    <Card data-testid="gs-slip-panel">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex items-start gap-2">
          {onBack && <Button size="icon" variant="ghost" onClick={onBack} data-testid="gs-slip-back-btn"><ArrowLeft className="w-4 h-4" /></Button>}
          <div className="min-w-0"><CardTitle className="text-base font-mono" data-testid="gs-slip-id">{slip.slip_id}</CardTitle>
            <p className="text-xs text-gray-500">{slip.jo_id} · <span className="font-mono">{slip.fg_name}</span> {slip.design_name} · to {sendToText(slip.order)}</p></div>
        </div>
        <JoBadge status={slip.status} testId="gs-slip-status" />
      </CardHeader>
      <CardContent className="space-y-4">
        <LineProgress slip={slip} />
        {['OPEN', 'PICKING'].includes(slip.status) && (
          <form onSubmit={onScan} className="flex gap-2 items-center">
            <ScanLine className="w-5 h-5 text-gray-500 shrink-0" />
            <Input ref={ref} value={scan} onChange={(e) => setScan(e.target.value)} placeholder="Scan bundle barcode" disabled={busy === 'pick'} className="h-11 flex-1 min-w-0 sm:max-w-md" data-testid="gs-slip-scan-input" />
            <Button type="submit" disabled={busy === 'pick'} data-testid="gs-slip-scan-btn">{busy === 'pick' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pick'}</Button>
          </form>
        )}
        <Slots slip={slip} live={live} onUnpick={(c) => run('unpick', () => joApi.unpick(slip.slip_id, c), `${c} put back`)} />
        {slip.status === 'PICKED' && (
          <Button disabled={busy === 'transfer'} onClick={() => run('transfer', () => joApi.transfer(slip.slip_id), (r) => `Internal Transfer ${r.transfer_id} made — ${r.bundles} bundle(s), ${r.pcs} pcs to Processing`)}
            className="bg-[#1A1F2E] hover:bg-black w-full sm:w-auto h-auto py-2 whitespace-normal" data-testid="gs-slip-transfer-btn">
            {busy === 'transfer' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}Make Internal Transfer to Processing ({slip.bundles.length} bundles · {fmtN(slip.pcs)} pcs)</Button>
        )}
        {slip.transfer_id && <p className="text-sm text-emerald-700" data-testid="gs-slip-transfer-id">Internal Transfer {slip.transfer_id} made — Processing assigns the unit in Inward Consignment.</p>}
        {live && slip.can_plan && (
          <div className="flex flex-wrap gap-2 items-center border-t pt-3">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason to cancel this slip" className="w-full sm:w-[260px]" data-testid="gs-slip-cancel-reason" />
            <Button variant="outline" className="text-rose-700" disabled={busy === 'cancel'} onClick={() => run('cancel', () => joApi.cancelSlip(slip.slip_id, reason), 'Pick slip cancelled — pieces back on the job order balance')} data-testid="gs-slip-cancel-btn">
              <XCircle className="w-4 h-4 mr-1" />Cancel pick slip</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GsSlipPanel;
